import Inputmask from 'inputmask';
import { COUNTRIES } from '../data/countries';

/* eslint-disable max-len */
const SELECT_SVG_ICON = `<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path id="Icon" d="M4.2157 6L8.2157 10L12.2157 6" stroke="#1E1E1E" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
/* eslint-enable max-len */

class ContactSection extends HTMLElement {
  constructor() {
    super();
    this.formSections = new Set();
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);

    const theme = window.theme || {};

    this.theme = theme;
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    this.initTabs();
    this.initForm();

    window.initCountries = this.initCountries.bind(this);
    window.initCustomSelect = this.initCustomSelect.bind(this);
    window.initTelField = this.initTelField.bind(this);
  }

  initTabs() {
    const tabsContainer = this.querySelector('.tabs');

    if (!tabsContainer) return;

    tabsContainer.addEventListener('click', (e) => {
      const tab = e.target.closest('.tabs-header-item');

      if (!tab) return;

      const index = tab.getAttribute('data-index');
      const allTabs = tabsContainer.querySelectorAll('.tabs-header-item');
      const contents = tabsContainer.querySelectorAll('.tabs-content > div');

      allTabs.forEach((t) => t.classList.toggle('active', t.getAttribute('data-index') === index));
      contents.forEach((c) => c.classList.toggle('hidden', c.getAttribute('data-index') !== index));
    });
  }

  initForm() {
    const contactForms = document.querySelectorAll('.contact-form');

    contactForms.forEach((form) => {
      const container = form.closest('[id]');

      if (container && container.id) {
        const { id } = container;

        if (!this.formSections.has(id)) {
          this.formSections.add(id);
          this.initCountries(id);
          this.initCustomSelect(id);
          this.initTelField(id);
        }
      }
    });
  }

  initCountries(id) {
    const countrySelect = document.getElementById(`${id}-country`);

    if (!countrySelect) return;

    // Use document fragment for better performance
    const fragment = document.createDocumentFragment();

    COUNTRIES.forEach((countryName) => {
      const option = document.createElement('option');

      option.value = countryName;
      option.textContent = countryName;

      if (countryName === 'United States') {
        option.selected = true;
      }

      fragment.appendChild(option);
    });

    countrySelect.appendChild(fragment);
  }

  initCustomSelect(id) {
    const selects = this.querySelectorAll(`.select-${id}`);

    if (!selects.length) return;

    selects.forEach((originalSelect) => {
      const hasSearchable = originalSelect.classList.contains('searchable') || originalSelect.options.length > 10;

      const customSelect = document.createElement('div');

      customSelect.className = 'custom-select';

      if (hasSearchable) {
        customSelect.classList.add('searchable');
      }

      const selectedOption = document.createElement('div');
      const selectedOptionText = document.createElement('div');
      const selectedOptionIcon = document.createElement('div');

      selectedOptionText.className = 'selected-option-text';
      selectedOptionIcon.className = 'selected-option-icon';
      selectedOptionIcon.innerHTML = SELECT_SVG_ICON;
      selectedOption.className = 'selected-option';

      selectedOption.setAttribute('tabindex', '0');
      selectedOption.setAttribute('role', 'combobox');
      selectedOption.setAttribute('aria-expanded', 'false');
      selectedOption.setAttribute('aria-haspopup', 'listbox');
      selectedOption.setAttribute('aria-label', originalSelect.getAttribute('aria-label') || 'Select an option');

      selectedOptionText.textContent = originalSelect.options[originalSelect.selectedIndex]?.text || 'Select an option';

      selectedOption.appendChild(selectedOptionText);
      selectedOption.appendChild(selectedOptionIcon);
      customSelect.appendChild(selectedOption);

      const srOnlyText = document.createElement('span');

      srOnlyText.className = 'sr-only';
      srOnlyText.textContent = 'Press Enter to open dropdown. Use arrow keys to navigate options.';
      selectedOption.appendChild(srOnlyText);

      const optionsContainer = document.createElement('div');

      optionsContainer.className = 'options-container';
      optionsContainer.setAttribute('role', 'listbox');

      const listboxId = `listbox-${id}-${Math.random().toString(36).substring(2, 9)}`;

      optionsContainer.id = listboxId;
      selectedOption.setAttribute('aria-controls', listboxId);

      if (hasSearchable) {
        const searchWrapper = document.createElement('div');

        searchWrapper.className = 'search-wrapper';

        const searchInput = document.createElement('input');

        searchInput.type = 'text';
        searchInput.className = 'search-input';
        searchInput.setAttribute('aria-label', theme.sections.contact_us.textarea.aria_label);

        searchInput.placeholder = theme.general.search.search;

        searchInput.addEventListener('click', (e) => {
          e.stopPropagation();
        });

        searchInput.addEventListener('input', (e) => {
          const searchText = e.target.value.toLowerCase();
          const options = optionsContainer.querySelectorAll('.option');

          options.forEach((option) => {
            const text = option.textContent.toLowerCase();
            const isVisible = text.includes(searchText);

            option.style.display = isVisible ? 'block' : 'none';
          });
        });

        searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            customSelect.classList.remove('active');
            selectedOption.setAttribute('aria-expanded', 'false');
            selectedOption.focus();
            e.preventDefault();
          } else if (e.key === 'ArrowDown') {
            const visibleOptions = [...optionsContainer.querySelectorAll('.option')].filter(
              (opt) => opt.style.display !== 'none'
            );

            if (visibleOptions.length > 0) {
              visibleOptions[0].focus();
              e.preventDefault();
            }
          } else if (e.key === 'Enter') {
            const visibleOptions = [...optionsContainer.querySelectorAll('.option')].filter(
              (opt) => opt.style.display !== 'none'
            );

            if (visibleOptions.length === 1) {
              visibleOptions[0].click();
              e.preventDefault();
            }
          }
        });

        searchWrapper.appendChild(searchInput);
        optionsContainer.appendChild(searchWrapper);
      }

      const fragment = document.createDocumentFragment();
      const optionsFragment = document.createElement('div');

      optionsFragment.className = 'options-list';

      Array.from(originalSelect.options).forEach((option) => {
        const customOption = document.createElement('div');

        customOption.className = 'option';
        customOption.textContent = option.text;
        customOption.dataset.value = option.value;
        customOption.tabIndex = 0;

        if (option.disabled && option.selected) {
          return;
        }

        customOption.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            customOption.click();
            e.preventDefault();
          } else if (e.key === 'Escape') {
            customSelect.classList.remove('active');
            selectedOption.focus();
            e.preventDefault();
          } else if (e.key === 'ArrowDown') {
            const visibleOptions = [...optionsFragment.querySelectorAll('.option')].filter(
              (opt) => opt.style.display !== 'none'
            );

            const currentIndex = visibleOptions.indexOf(customOption);

            if (currentIndex < visibleOptions.length - 1) {
              visibleOptions[currentIndex + 1].focus();
            }

            e.preventDefault();
          } else if (e.key === 'ArrowUp') {
            const visibleOptions = [...optionsFragment.querySelectorAll('.option')].filter(
              (opt) => opt.style.display !== 'none'
            );

            const currentIndex = visibleOptions.indexOf(customOption);

            if (currentIndex > 0) {
              visibleOptions[currentIndex - 1].focus();
            } else if (hasSearchable) {
              optionsContainer.querySelector('.search-input').focus();
            }

            e.preventDefault();
          }
        });

        customOption.addEventListener('click', () => {
          selectedOptionText.textContent = customOption.textContent;
          originalSelect.value = customOption.dataset.value;

          const event = new Event('change', { bubbles: true });

          originalSelect.dispatchEvent(event);

          customSelect.classList.remove('active');

          if (hasSearchable) {
            const searchInput = optionsContainer.querySelector('.search-input');

            if (searchInput) {
              searchInput.value = '';

              optionsFragment.querySelectorAll('.option').forEach((opt) => {
                opt.style.display = 'block';
              });
            }
          }
        });

        fragment.appendChild(customOption);
      });

      optionsFragment.appendChild(fragment);
      optionsContainer.appendChild(optionsFragment);
      customSelect.appendChild(optionsContainer);

      originalSelect.insertAdjacentElement('beforebegin', customSelect);
      originalSelect.style.display = 'none';

      selectedOption.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();

          if (!customSelect.classList.contains('active')) {
            document.querySelectorAll('.custom-select').forEach((otherSelect) => {
              if (otherSelect !== customSelect) {
                otherSelect.classList.remove('active');
                otherSelect.querySelector('.selected-option')?.setAttribute('aria-expanded', 'false');
              }
            });

            customSelect.classList.add('active');
            selectedOption.setAttribute('aria-expanded', 'true');

            if (hasSearchable) {
              setTimeout(() => {
                const searchInput = optionsContainer.querySelector('.search-input');

                if (searchInput) {
                  searchInput.focus();
                }
              }, 10);
            } else {
              setTimeout(() => {
                const firstOption = optionsContainer.querySelector('.option');

                if (firstOption) {
                  firstOption.focus();
                }
              }, 10);
            }
          } else {
            customSelect.classList.remove('active');
            selectedOption.setAttribute('aria-expanded', 'false');
          }
        }
      });

      selectedOption.addEventListener('click', () => {
        document.querySelectorAll('.custom-select').forEach((otherSelect) => {
          if (otherSelect !== customSelect) {
            otherSelect.classList.remove('active');
            otherSelect.querySelector('.selected-option')?.setAttribute('aria-expanded', 'false');
          }
        });

        customSelect.classList.toggle('active');
        const isActive = customSelect.classList.contains('active');

        selectedOption.setAttribute('aria-expanded', isActive ? 'true' : 'false');

        if (isActive && hasSearchable) {
          setTimeout(() => {
            const searchInput = optionsContainer.querySelector('.search-input');

            if (searchInput) {
              searchInput.focus();
            }
          }, 10);
        }
      });
    });

    if (!this.hasOutsideClickListener) {
      document.addEventListener('click', this.boundHandleOutsideClick);
      this.hasOutsideClickListener = true;
    }
  }

  handleOutsideClick(e) {
    document.querySelectorAll('.custom-select').forEach((customSelect) => {
      if (!customSelect.contains(e.target)) {
        customSelect.classList.remove('active');
        customSelect.querySelector('.selected-option')?.setAttribute('aria-expanded', 'false');
      }
    });
  }

  initTelField(id) {
    const phoneInput = document.getElementById(`${id}-phone`);

    if (!phoneInput) return;

    Inputmask({
      mask: '(999) 999-9999',
      showMaskOnHover: false,
      clearMaskOnLostFocus: true,
    }).mask(phoneInput);
  }

  disconnectedCallback() {
    if (this.hasOutsideClickListener) {
      document.removeEventListener('click', this.boundHandleOutsideClick);
    }

    this.formSections.forEach((id) => {
      const phoneInput = document.getElementById(`${id}-phone`);

      if (phoneInput && phoneInput.inputmask) {
        phoneInput.inputmask.remove();
      }
    });

    delete window.initCountries;
    delete window.initCustomSelect;
    delete window.initTelField;
  }
}

customElements.define('contact-section', ContactSection);
